import { useEffect, useState, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { useIonToast } from "@ionic/react";
import { useIntl } from "react-intl";
import Nav from "react-bootstrap/Nav";
import { IconSettings } from "@tabler/icons-react";
import config from "../../config";
import { Integration } from "../../interfaces";
import Api from "../../api/integrations";

interface Properties {
  webchat: Integration | undefined;
  isMobile: boolean;
}

type LaunchMode = "webchat" | "whatsapp";

import styles from "./styles.module.css";

const DEFAULT_DESKTOP_WIDTH = 460;
const DEFAULT_DESKTOP_HEIGHT = 850;
const DEFAULT_MOBILE_WIDTH = 100;
const DEFAULT_MOBILE_HEIGHT = 100;

const toInt = (value: string, fallback: number) => {
  if (value.trim() === "") return fallback;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.round(parsed);
};

const WebChat = (props: Properties) => {
  const [apiKey, setApiKey] = useState("");
  const [bubbleColor, setBubbleColor] = useState("#007a61");
  const [desktopWidth, setDesktopWidth] = useState(DEFAULT_DESKTOP_WIDTH);
  const [desktopHeight, setDesktopHeight] = useState(DEFAULT_DESKTOP_HEIGHT);
  const [mobileWidth, setMobileWidth] = useState(DEFAULT_MOBILE_WIDTH);
  const [mobileHeight, setMobileHeight] = useState(DEFAULT_MOBILE_HEIGHT);
  const [launchMode, setLaunchMode] = useState<LaunchMode>("webchat");
  const [whatsappConnectionId, setWhatsappConnectionId] = useState<
    string | null
  >(null);
  const [whatsappOptions, setWhatsappOptions] = useState<
    { id: string; name: string; phoneNumber?: string; active?: boolean }[]
  >([]);
  const [isSavingColor, setIsSavingColor] = useState(false);
  const [tab, setTab] = useState("embed");
  const intl = useIntl();
  const [present] = useIonToast();

  useEffect(() => {
    if (props.webchat) {
      const accounts = props.webchat.accounts;
      if (accounts && accounts.length > 0) {
        const account = accounts[0];
        if (account.apiKey) {
          setApiKey(account.apiKey);
        }
        setBubbleColor(account.bubbleColor || "#007a61");
        setLaunchMode(
          account.launchMode === "whatsapp" ? "whatsapp" : "webchat",
        );
        setDesktopWidth(account.desktopWidth || DEFAULT_DESKTOP_WIDTH);
        setDesktopHeight(account.desktopHeight || DEFAULT_DESKTOP_HEIGHT);
        setMobileWidth(account.mobileWidth || DEFAULT_MOBILE_WIDTH);
        setMobileHeight(account.mobileHeight || DEFAULT_MOBILE_HEIGHT);
        setWhatsappConnectionId(account.whatsappConnectionId || null);
        setWhatsappOptions(account.whatsappOptions || []);
      }
    }
  }, [props.webchat]);

  const url = `${config.HTML_ROOT}/web-chat?id=${apiKey}`;
  const onCopy = async () => {
    await navigator.clipboard.writeText(url);
    return present({
      message: intl.formatMessage({ id: "common.copyLink" }),
      duration: 6000,
      color: "light",
      position: "top",
    });
  };
  const embedCode = `<script>(function(){const onLoad=function(){const script=document.createElement("script");script.src="${config.API_ROOT}/embed.min.js";script.id="visitowc";script.setAttribute("data-domain","visitoai.com");script.setAttribute("data-apiKey","${apiKey}");document.body.appendChild(script);};if(document.readyState==="complete"){onLoad();}else{window.addEventListener("load",onLoad);}})();</script>`;
  const onCopyEmbed = async () => {
    await navigator.clipboard.writeText(embedCode);
    return present({
      message: intl.formatMessage({ id: "common.copyCode" }),
      duration: 6000,
      color: "light",
      position: "top",
    });
  };

  const onCopyApiKey = async () => {
    await navigator.clipboard.writeText(apiKey);
    return present({
      message: intl.formatMessage({ id: "common.copyCode" }),
      duration: 6000,
      color: "light",
      position: "top",
    });
  };
  const onOpen = async () => {
    window.open(url, "_blank");
  };

  const tabs: { key: string; labelId: string; icon?: ReactNode }[] = [
    { key: "embed", labelId: "common.embed" },
    { key: "share", labelId: "common.share" },
    { key: "wordpress", labelId: "common.wordpress" },
    {
      key: "config",
      labelId: "common.configure",
      icon: <IconSettings size={14} />,
    },
  ];

  const onSaveSettings = async () => {
    setIsSavingColor(true);
    const { data, err } = await Api.updateWebchatSettings({
      bubbleColor,
      launchMode,
      whatsappConnectionId:
        launchMode === "whatsapp" ? whatsappConnectionId : null,
      desktopWidth,
      desktopHeight,
      mobileWidth,
      mobileHeight,
    });
    setIsSavingColor(false);

    if (err || !data?.success) {
      return present({
        message:
          err?.[intl.locale] || intl.formatMessage({ id: "common.tryItLater" }),
        duration: 6000,
        color: "warning",
        position: "top",
      });
    }

    setBubbleColor(data?.webChat?.bubbleColor || bubbleColor);
    setLaunchMode(
      data?.webChat?.launchMode === "whatsapp" ? "whatsapp" : "webchat",
    );
    setDesktopWidth(data?.webChat?.desktopWidth || desktopWidth);
    setDesktopHeight(data?.webChat?.desktopHeight || desktopHeight);
    setMobileWidth(data?.webChat?.mobileWidth || mobileWidth);
    setMobileHeight(data?.webChat?.mobileHeight || mobileHeight);
    setWhatsappConnectionId(data?.webChat?.whatsappConnectionId || null);
    return present({
      message: intl.formatMessage({ id: "common.save" }),
      duration: 3500,
      color: "success",
      position: "top",
    });
  };

  const launchTarget =
    launchMode === "whatsapp" && whatsappConnectionId
      ? `wa:${whatsappConnectionId}`
      : "webchat";

  const onChangeLaunchTarget = (value: string) => {
    if (value === "webchat") {
      setLaunchMode("webchat");
      return;
    }
    if (value.startsWith("wa:")) {
      setLaunchMode("whatsapp");
      setWhatsappConnectionId(value.replace("wa:", ""));
    }
  };

  return apiKey ? (
    <>
      <div className={props.isMobile ? "view-pane" : "visito-card"}>
        <div className={props.isMobile ? "view-pane-body" : "visito-card-body"}>
          <div className={`${styles.titleDisplay}`}>
            <div className={`${styles.titleItem} ${styles.logo}`}>
              <div className={`${styles.logo} web-logo`}></div>
            </div>
            <h3 className="fs-3">
              <FormattedMessage id={`integrations.webChatName`} />
            </h3>
          </div>
          <Nav
            variant="tabs"
            defaultActiveKey="/home"
            className={`${styles.navTabs} mb-3 mt-3`}
          >
            {tabs.map((t) => (
              <Nav.Item key={t.key}>
                <Nav.Link
                  active={t.key === tab}
                  className=""
                  onClick={() => setTab(t.key)}
                >
                  <span className="d-flex align-items-center gap-1">
                    {t.icon || null}
                    <FormattedMessage id={t.labelId} />
                  </span>
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
          {(() => {
            switch (tab) {
              case "embed":
                return (
                  <>
                    <h3 className="fs-4">
                      <FormattedMessage id="integrations.embedChat" />
                    </h3>
                    <p
                      className="fs-3 mt-3 color-step-600"
                      style={props.isMobile ? {} : { textWrap: "nowrap" }}
                    >
                      <FormattedMessage id="integrations.embedChatDescription" />
                    </p>
                    <pre className={`${styles.webChatUrl} fs-2 mt-2`}>
                      {embedCode}
                    </pre>
                    <div className="flex gap-2 mt-2">
                      <button
                        className="btn-visito-primary btn btn-sm"
                        onClick={onCopyEmbed}
                      >
                        <FormattedMessage id="integrations.copyLink" />
                      </button>
                    </div>
                  </>
                );
              case "config":
                return (
                  <>
                    <h3 className="fs-4 d-flex align-items-center gap-1">
                      <IconSettings size={18} />
                      <FormattedMessage id="common.configure" />
                    </h3>
                    <div className="mt-3">
                      <label
                        htmlFor="webchat-launch-target"
                        className="fs-3 color-step-700"
                      >
                        <FormattedMessage id="integrations.webChatLaunchTarget" />
                      </label>

                      <select
                        id="webchat-launch-target"
                        className="form-select mt-1"
                        value={launchTarget}
                        onChange={(e) => onChangeLaunchTarget(e.target.value)}
                      >
                        <option value="webchat">
                          {intl.formatMessage({
                            id: "integrations.webChatLaunchWebchat",
                          })}
                        </option>
                        {whatsappOptions
                          .filter((option) => option.active !== false)
                          .map((option) => (
                            <option key={option.id} value={`wa:${option.id}`}>
                              {`${intl.formatMessage({
                                id: "integrations.webChatLaunchWhatsappPrefix",
                              })} ${option.phoneNumber || option.name}`}
                            </option>
                          ))}
                      </select>
                      <p className="fs-2 mt-1 color-step-500 mb-0">
                        <FormattedMessage id="integrations.webChatLaunchTargetDescription" />
                      </p>
                    </div>
                    <div className="mt-3">
                      <label
                        htmlFor="webchat-bubble-color"
                        className="fs-3 color-step-700"
                      >
                        <FormattedMessage id="integrations.webChatBubbleColor" />
                      </label>
                      <div className="flex align-items-center gap-2 mt-1">
                        <input
                          id="webchat-bubble-color"
                          type="color"
                          value={bubbleColor}
                          onChange={(e) => setBubbleColor(e.target.value)}
                          style={{
                            width: "40px",
                            height: "32px",
                            border: "none",
                            background: "transparent",
                            padding: 0,
                            cursor: "pointer",
                          }}
                        />
                        <span className="fs-3 color-step-700">
                          {bubbleColor}
                        </span>
                      </div>
                      <p className="fs-2 mt-1 color-step-500 mb-0">
                        <FormattedMessage id="integrations.webChatBubbleColorDescription" />
                      </p>
                    </div>
                    <div className="mt-3">
                      <label className="fs-3 color-step-700">
                        <FormattedMessage id="integrations.webChatWidgetDesktopSize" />
                      </label>
                      <div className={`mt-1 ${styles.sizeGrid}`}>
                        <div>
                          <label
                            htmlFor="webchat-desktop-width"
                            className="fs-2 color-step-500"
                          >
                            <FormattedMessage id="integrations.webChatWidgetWidthPx" />
                          </label>
                          <input
                            id="webchat-desktop-width"
                            type="number"
                            min={280}
                            max={1000}
                            className="form-control mt-1"
                            value={desktopWidth}
                            onChange={(e) =>
                              setDesktopWidth(
                                toInt(e.target.value, DEFAULT_DESKTOP_WIDTH),
                              )
                            }
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="webchat-desktop-height"
                            className="fs-2 color-step-500"
                          >
                            <FormattedMessage id="integrations.webChatWidgetHeightPx" />
                          </label>
                          <input
                            id="webchat-desktop-height"
                            type="number"
                            min={400}
                            max={1400}
                            className="form-control mt-1"
                            value={desktopHeight}
                            onChange={(e) =>
                              setDesktopHeight(
                                toInt(e.target.value, DEFAULT_DESKTOP_HEIGHT),
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="fs-3 color-step-700">
                        <FormattedMessage id="integrations.webChatWidgetMobileSize" />
                      </label>
                      <div className={`mt-1 ${styles.sizeGrid}`}>
                        <div>
                          <label
                            htmlFor="webchat-mobile-width"
                            className="fs-2 color-step-500"
                          >
                            <FormattedMessage id="integrations.webChatWidgetWidthVw" />
                          </label>
                          <input
                            id="webchat-mobile-width"
                            type="number"
                            min={50}
                            max={100}
                            className="form-control mt-1"
                            value={mobileWidth}
                            onChange={(e) =>
                              setMobileWidth(
                                toInt(e.target.value, DEFAULT_MOBILE_WIDTH),
                              )
                            }
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="webchat-mobile-height"
                            className="fs-2 color-step-500"
                          >
                            <FormattedMessage id="integrations.webChatWidgetHeightVh" />
                          </label>
                          <input
                            id="webchat-mobile-height"
                            type="number"
                            min={50}
                            max={100}
                            className="form-control mt-1"
                            value={mobileHeight}
                            onChange={(e) =>
                              setMobileHeight(
                                toInt(e.target.value, DEFAULT_MOBILE_HEIGHT),
                              )
                            }
                          />
                        </div>
                      </div>
                      <p className="fs-2 mt-1 color-step-500 mb-0">
                        <FormattedMessage id="integrations.webChatWidgetSizeDescription" />
                      </p>
                    </div>
                    <div className={styles.settingsSaveRow}>
                      <button
                        className="btn-visito-primary btn"
                        onClick={onSaveSettings}
                        disabled={isSavingColor}
                      >
                        {isSavingColor ? (
                          "..."
                        ) : (
                          <FormattedMessage id="common.save" />
                        )}
                      </button>
                    </div>
                  </>
                );
              case "wordpress":
                return (
                  <>
                    <h3 className="fs-4">
                      <FormattedMessage id="integrations.wordpress" />
                    </h3>
                    <p className="fs-3 mt-3 color-step-600">
                      <FormattedMessage id="integrations.wordpressInstructions" />
                    </p>
                    <ol
                      className="fs-3 mt-3 color-step-600"
                      style={{ paddingLeft: "1.5rem" }}
                    >
                      <li>
                        <FormattedMessage id="integrations.wordpressStep1" />{" "}
                        <a
                          href="https://wordpress.com/plugins/visito-ai"
                          target="_blank"
                        >
                          https://wordpress.com/plugins/visito-ai
                        </a>
                      </li>
                      <li>
                        <FormattedMessage id="integrations.wordpressStep2" />
                      </li>
                    </ol>
                    <pre className={`${styles.webChatUrl} fs-2 mt-2`}>
                      {apiKey}
                    </pre>
                    <div className="flex gap-2 mt-2">
                      <button
                        className="btn-visito-primary btn btn-sm"
                        onClick={onCopyApiKey}
                      >
                        <FormattedMessage id="integrations.copyLink" />
                      </button>
                    </div>
                  </>
                );

              default:
                return (
                  <>
                    <h3 className="fs-4">
                      <FormattedMessage id="integrations.webChat" />
                    </h3>
                    <p
                      className="fs-3 mt-3 color-step-600"
                      style={props.isMobile ? {} : { textWrap: "nowrap" }}
                    >
                      <FormattedMessage id="integrations.webChatDescription" />
                    </p>
                    <pre className={`${styles.webChatUrl} fs-2 mt-2`}>
                      {url}
                    </pre>
                    <div className="flex gap-2 mt-2">
                      <button
                        className="btn-visito-primary btn btn-sm"
                        onClick={onCopy}
                      >
                        <FormattedMessage id="integrations.copyLink" />
                      </button>
                      <button
                        className="btn-visito-primary btn btn-sm"
                        onClick={onOpen}
                      >
                        <FormattedMessage id="integrations.visitLink" />
                      </button>
                    </div>
                  </>
                );
            }
          })()}
        </div>
      </div>
    </>
  ) : null;
};

export default WebChat;
